import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useState } from 'react'

import palette from 'constants/palette'
import { Icon } from 'controlled-components'
import { buildTranslate } from 'locales'
import { currencyFromCents } from 'utilities/formatters'

import locationNameOf from './locationNameOf'
import NoMatchBadge from './NoMatchBadge'
import ProviderBadge from './ProviderBadge'
import type { PriceRange, SelectionUids, UidLine, UidRow } from './versionTypes'

const t = buildTranslate('components.ai.picker.canonical.versions')
const tPaste = buildTranslate('components.ai.picker.canonical.paste')
const tGlobal = buildTranslate('components.ai.picker.canonical.global')

const COLUMNS = ['provider', 'name', 'uid', 'price', 'locations'] as const
const CHEVRON_SIZE = 16
const SKELETON_ROWS = 3
const SKELETON_HEIGHT = '14px'

export const priceLabel = (range: PriceRange | null): string => {
  if (!range) return t('no_price')
  const min = currencyFromCents(range.minCents)
  if (range.minCents === range.maxCents) return min
  return t('price_range', { min, max: currencyFromCents(range.maxCents) })
}

type Locations = {
  names: ReadonlyMap<number, string>
  namesFailed: boolean
}

type Classes = { cell: string; uid: string; line: string }

const LineRow = ({
  id,
  line,
  provider,
  locations,
  classes,
}: {
  id: string
  line: UidLine
  provider: string
  locations: Locations
  classes: Classes
}) => (
  <tr id={id} className={classes.line}>
    <td className={classes.cell}>
      <ProviderBadge provider={provider} />
    </td>
    <td className={classes.cell}>{line.name}</td>
    <td className={`${classes.cell} ${classes.uid}`}>{line.uid}</td>
    <td className={`${classes.cell} text-nowrap`}>
      {line.priceCents !== null && Number.isFinite(line.priceCents)
        ? currencyFromCents(line.priceCents)
        : t('no_price')}
    </td>
    <td className={classes.cell}>
      {locationNameOf(line.locationId, locations.names, locations.namesFailed)}
    </td>
  </tr>
)

/** A UID group or a store UID; a Global opens to a line per store UID and location */
const UidRowGroup = ({
  row,
  locations,
  classes,
}: {
  row: UidRow
  locations: Locations
  classes: Classes
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const lineIdOf = (line: UidLine) => `uid-line-${row.id}-${line.id}`
  const lines = [...row.lines].sort((a, b) =>
    locationNameOf(
      a.locationId,
      locations.names,
      locations.namesFailed
    ).localeCompare(
      locationNameOf(b.locationId, locations.names, locations.namesFailed)
    )
  )
  const location =
    row.locationIds.length === 1
      ? locationNameOf(
          row.locationIds[0],
          locations.names,
          locations.namesFailed
        )
      : t('locations_count', { count: row.locationIds.length })

  return (
    <>
      <tr>
        <td className={classes.cell}>
          <ProviderBadge provider={row.provider} />
        </td>
        <th scope="row" className={`${classes.cell} font-weight-normal`}>
          {row.name}
        </th>
        <td className={classes.cell}>
          <span className="d-inline-flex align-items-center flex-wrap">
            {row.isGlobal && (
              <span className="text-nowrap mr-xs">{tGlobal('uid_label')}</span>
            )}
            <span className={classes.uid}>{row.uid}</span>
          </span>
        </td>
        <td className={`${classes.cell} text-nowrap`}>
          {priceLabel(row.priceRange)}
        </td>
        <td className={classes.cell}>
          <span className="d-flex align-items-center">
            {location}
            {lines.length > 0 && (
              <Button
                kind="minimal"
                size="small"
                className="p-0 ml-auto"
                aria-expanded={isExpanded}
                aria-controls={lines.map(lineIdOf).join(' ')}
                aria-label={t('row_toggle', { name: row.name })}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Icon
                  name={isExpanded ? 'chevron-down' : 'chevron-right'}
                  size={CHEVRON_SIZE}
                  aria-hidden="true"
                />
              </Button>
            )}
          </span>
        </td>
      </tr>
      {isExpanded &&
        lines.map(line => (
          <LineRow
            key={line.id}
            id={lineIdOf(line)}
            line={line}
            provider={row.provider}
            locations={locations}
            classes={classes}
          />
        ))}
    </>
  )
}

const UnmatchedRows = ({
  uids,
  classes,
}: {
  uids: string[]
  classes: Classes
}) => (
  <tbody>
    <tr>
      <th
        scope="rowgroup"
        colSpan={COLUMNS.length}
        className={`${classes.cell} text-left`}
      >
        <Text.BodyText3 bold color="grey90" tag="div">
          {t('unmatched_section')}
        </Text.BodyText3>
        <Text.BodyText5 color="grey70">
          {tPaste('add_anyway_warning')}
        </Text.BodyText5>
      </th>
    </tr>
    {uids.map(uid => (
      <tr key={uid}>
        <td className={classes.cell}>
          <NoMatchBadge />
        </td>
        <th scope="row" className={`${classes.cell} font-weight-normal`}>
          {uid}
        </th>
        <td className={`${classes.cell} ${classes.uid}`}>{uid}</td>
        <td className={`${classes.cell} text-nowrap`}>{priceLabel(null)}</td>
        <td className={classes.cell}>{t('locations_count', { count: 0 })}</td>
      </tr>
    ))}
  </tbody>
)

type Props = {
  /** null while the UIDs load */
  uids: SelectionUids | null
  locationNames: ReadonlyMap<number, string>
  locationsFailed: boolean
}

/** Every UID inside a selection: provider, name, UID, price and locations */
const VersionsTable = ({ uids, locationNames, locationsFailed }: Props) => {
  const [css] = useStyletron()
  const locations: Locations = {
    names: locationNames,
    namesFailed: locationsFailed,
  }
  const classes: Classes = {
    cell: `p-s align-top ${css({
      borderBottom: `1px solid ${palette.grey30}`,
    })}`,
    uid: css({ fontFamily: 'monospace', wordBreak: 'break-all' }),
    line: css({ backgroundColor: palette.grey05 }),
  }

  const renderRows = (rows: UidRow[]) =>
    rows.map(row => (
      <UidRowGroup
        key={row.id}
        row={row}
        locations={locations}
        classes={classes}
      />
    ))

  const renderBody = () => {
    if (!uids) {
      return (
        <tbody aria-hidden="true">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <tr key={index}>
              {COLUMNS.map(column => (
                <td key={column} className={classes.cell}>
                  <div
                    className={css({
                      height: SKELETON_HEIGHT,
                      borderRadius: '4px',
                      backgroundColor: palette.grey20,
                    })}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      )
    }
    if (uids.mode === 'specific') {
      return (
        <>
          <tbody>{renderRows(uids.rows)}</tbody>
          {uids.unmatchedUids.length > 0 && (
            <UnmatchedRows uids={uids.unmatchedUids} classes={classes} />
          )}
        </>
      )
    }
    return uids.products.map(product => (
      <tbody key={product.id}>
        <tr>
          <th
            scope="rowgroup"
            colSpan={COLUMNS.length}
            className={`${classes.cell} text-left`}
          >
            <Text.BodyText3 bold color="grey90">
              {product.name}
            </Text.BodyText3>
          </th>
        </tr>
        {product.rows.length === 0 ? (
          <tr>
            <td colSpan={COLUMNS.length} className={classes.cell}>
              {t('product_empty')}
            </td>
          </tr>
        ) : (
          renderRows(product.rows)
        )}
      </tbody>
    ))
  }

  return (
    <div className={css({ overflowX: 'auto' })}>
      <table
        aria-busy={!uids}
        className={`body-text-4 grey-90 w-100 ${css({
          borderCollapse: 'collapse',
        })}`}
      >
        <caption className="sr-only">{t('caption')}</caption>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <th
                key={column}
                scope="col"
                className={`${classes.cell} text-left text-nowrap`}
              >
                {t(`columns.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        {renderBody()}
      </table>
    </div>
  )
}

export default VersionsTable
